import type { ElementType } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type MetricTone = 'neutral' | 'warning' | 'success' | 'danger' | 'info';

export interface PluginMetricCardItem {
  id: string;
  label: string;
  value: string | number;
  icon: ElementType;
  detail?: string;
  tone?: MetricTone;
  onClick?: () => void;
  valueClassName?: string;
}

interface PluginMetricCardsProps {
  items: PluginMetricCardItem[];
  loading?: boolean;
  className?: string;
}

const TONE_CLASSES: Record<MetricTone, { bg: string; fg: string }> = {
  neutral: { bg: 'bg-accent', fg: 'text-accent-foreground' },
  warning: { bg: 'bg-[hsl(var(--status-warning)/0.15)]', fg: 'text-[hsl(var(--status-warning))]' },
  success: { bg: 'bg-[hsl(var(--status-ok)/0.15)]', fg: 'text-[hsl(var(--status-ok))]' },
  danger: { bg: 'bg-[hsl(var(--status-error)/0.15)]', fg: 'text-[hsl(var(--status-error))]' },
  info: { bg: 'bg-primary/10', fg: 'text-primary' },
};

export function PluginMetricCards({ items, loading = false, className }: PluginMetricCardsProps) {
  return (
    <div className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}>
      {items.map(({ id, label, value, icon: Icon, detail, tone = 'neutral', onClick, valueClassName }) => {
        const toneClasses = TONE_CLASSES[tone];
        const content = (
          <Card className="border-border shadow-sm">
            <CardContent className="flex items-center gap-3 p-4">
              <div className={cn('rounded-lg p-2.5', toneClasses.bg)}>
                <Icon className={cn('h-4 w-4', toneClasses.fg)} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{label}</p>
                {loading ? (
                  <div className="mt-1 h-7 w-16 animate-pulse rounded bg-muted" />
                ) : (
                  <div className="mt-1 flex min-w-0 items-baseline gap-2">
                    <p className={cn('shrink-0 text-lg font-semibold leading-tight tabular-nums text-foreground', valueClassName)}>
                      {typeof value === 'number' ? value.toLocaleString('de-DE') : value}
                    </p>
                    {detail ? <p className="min-w-0 truncate text-[11px] text-muted-foreground">{detail}</p> : null}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );

        if (!onClick) {
          return <div key={id}>{content}</div>;
        }

        return (
          <button
            key={id}
            type="button"
            className="text-left transition-transform hover:-translate-y-0.5"
            onClick={onClick}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}